// Face validation (face templates spec section 5): faces, atlases and art. Shared by the client
// loader and the Node validator.

import { Checker, ID, type Issue } from '../presentation/validate.ts';
import { BUILTIN_GLYPHS, COLOR_TOKENS, type ThemeV2 } from '../presentation/types.ts';
import { GAME_ICONS } from '../icons/gameIcons.ts';
import { COLOR_ICONS } from '../icons/colorIcons.ts';
import { isBuiltin } from './builtin.ts';
import { binding, flatten, matchFace } from './resolve.ts';
import { LODS, type ArtFile, type AtlasesFile, type FaceDef, type FacesFile, type Layer, type Paint } from './types.ts';
import { ART_PATTERNS, frameIndex } from './art.ts';
import { parseRichText } from './richText.ts';

const LAYER_FIELDS: Record<string, string[]> = {
  fill: ['paint'],
  image: ['art', 'asset', 'fit', 'focus'],
  text: ['text', 'rich', 'padding', 'lineHeight', 'role', 'ink', 'align', 'valign', 'fit', 'style', 'effects', 'scale'],
  icon: ['icon', 'paint'],
  'icon-row': ['source', 'iconSize', 'align', 'gap', 'style', 'numberKeys', 'paint'],
  badge: ['text', 'icon', 'shape', 'paint', 'ink', 'effects'],
  frame: ['style', 'paint', 'width'],
  shape: ['shape', 'paint', 'stroke'],
  pattern: ['pattern', 'paint', 'scale'],
};
const REQUIRED: Record<string, string[]> = { fill: ['paint'], text: ['text'], icon: ['icon'], 'icon-row': ['source'], shape: ['shape'], pattern: ['pattern'] };
const COMMON = ['type', 'id', 'box', 'opacity', 'blend', 'rotate', 'mask', 'when', 'unless', 'lod'];
const HEX_PAINT = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const ENUMS: Record<string, readonly string[]> = {
  fit: ['cover', 'contain', 'fill', 'none'], role: ['title', 'subtitle', 'rules', 'flavor', 'number', 'caption'],
  align: ['start', 'center', 'end'], valign: ['top', 'middle', 'bottom'], style: [], blend: ['normal', 'multiply', 'screen', 'overlay', 'soft-light'],
};
const TEXT_FIT = ['shrink', 'clamp', 'wrap'];
const TEXT_STYLE = ['normal', 'italic', 'caps'];
const ROW_STYLE = ['count-icon', 'icon-count', 'repeat', 'symbols'];
const BADGE_SHAPES = ['circle', 'shield', 'banner', 'hex', 'star', 'pill'];
const FRAME_STYLES = ['classic', 'ornate', 'minimal', 'tarot', 'notched', 'double'];
const SHAPES = ['rect', 'circle', 'hex', 'diamond', 'arch', 'ribbon'];
const PATTERNS = ['stripes', 'dots', 'grid', 'crosshatch', 'noise', 'wood', 'linen'];
const MASKS = /^(none|rounded|circle|arch|hex|diamond|art:[A-Za-z0-9_-]{1,32})$/;

function bindingOk(c: Checker, text: unknown, path: string, theme?: ThemeV2): boolean {
  if (!c.string(text, path, 160)) return false;
  const b = binding(text as string);
  if (typeof b === 'string') { c.error(path, b); return false; }
  for (const f of b.formatters) if (f.kind === 'map' && theme && !theme.maps?.[f.name]) c.error(path, `Theme map "${f.name}" does not exist.`);
  return true;
}

function paint(c: Checker, p: unknown, path: string, theme?: ThemeV2): void {
  if (typeof p === 'string') {
    if (HEX_PAINT.test(p) || (COLOR_TOKENS as readonly string[]).includes(p)) return;
    bindingOk(c, p, path, theme);
    return;
  }
  const g = p as Exclude<Paint, string>;
  if (!c.object(g, path, [], ['linear', 'radial', 'angle', 'at'])) return;
  const list = 'linear' in g ? g.linear : 'radial' in g ? g.radial : undefined;
  if (('linear' in g) === ('radial' in g)) { c.error(path, 'A gradient has exactly one of linear or radial.'); return; }
  if (!c.array(list, path + ('linear' in g ? '.linear' : '.radial'), 1, 6)) return;
  list!.forEach((s, i) => {
    const sp = `${path}.stops[${i}]`;
    if (Array.isArray(s)) { paint(c, s[0], sp, theme); c.number(s[1], `${sp}[1]`, 0, 1); }
    else paint(c, s, sp, theme);
  });
  if ('angle' in g && g.angle !== undefined) c.number(g.angle, `${path}.angle`, -360, 360);
  if ('at' in g && g.at !== undefined && (!Array.isArray(g.at) || g.at.length !== 2)) c.error(`${path}.at`, 'Expected [x, y].');
}

function icon(c: Checker, text: unknown, path: string, theme?: ThemeV2): void {
  if (!c.string(text, path, 160)) return;
  const t = text as string;
  if (t.startsWith('gi:')) { if (!(t.slice(3) in GAME_ICONS)) c.error(path, `Library icon "${t}" is not bundled.`); return; }
  if (t.startsWith('svg:')) { if (!(t.slice(4) in COLOR_ICONS)) c.error(path, `Color SVG icon "${t}" is not bundled.`); return; }
  if (t.includes('|') || /^(fields|catalog|object)/.test(t)) { bindingOk(c, t, path, theme); return; }
  if (!theme?.icons?.[t] && !(BUILTIN_GLYPHS as readonly string[]).includes(t)) c.warn(path, `Icon "${t}" is not in the theme; a labelled circle is drawn.`);
}

function layer(c: Checker, l: Layer, path: string, theme: ThemeV2 | undefined, inAdd: boolean, back: boolean, richAllowed = true): void {
  if (!l || typeof l !== 'object') { c.error(path, 'Expected a layer.'); return; }
  const fields = LAYER_FIELDS[l.type as string];
  if (!fields) { c.error(`${path}.type`, `Unknown layer type "${String(l.type)}".`); return; }
  if (!c.object(l, path, ['type', ...(REQUIRED[l.type] ?? [])], [...COMMON, ...fields, ...(inAdd ? ['after', 'before'] : [])].filter(k => k !== 'type' && !(REQUIRED[l.type] ?? []).includes(k)))) return;
  const L = l as unknown as Record<string, unknown>;
  if (l.id !== undefined) c.string(l.id, `${path}.id`, 64, ID);
  if (l.box !== undefined) {
    if (!Array.isArray(l.box) || l.box.length !== 4 || l.box.some(v => typeof v !== 'number' || v < 0 || v > 100)) c.error(`${path}.box`, 'Expected [x1, y1, x2, y2] in 0..100.');
    else if (l.box[2] < l.box[0] || l.box[3] < l.box[1]) c.error(`${path}.box`, 'x2 and y2 must not be less than x1 and y1.');
  }
  if (l.opacity !== undefined) c.number(l.opacity, `${path}.opacity`, 0, 1);
  if (l.blend !== undefined) c.oneOf(l.blend, `${path}.blend`, ENUMS.blend!);
  if (l.rotate !== undefined) c.number(l.rotate, `${path}.rotate`, -180, 180);
  if (l.mask !== undefined && (typeof l.mask !== 'string' || !MASKS.test(l.mask))) c.error(`${path}.mask`, 'Use none, rounded, circle, arch, hex, diamond or art:<slot>.');
  if (l.when !== undefined) bindingOk(c, l.when, `${path}.when`, theme);
  if (l.unless !== undefined) bindingOk(c, l.unless, `${path}.unless`, theme);
  if (l.lod !== undefined && c.array(l.lod, `${path}.lod`, 1, 4)) l.lod.forEach((v, i) => c.oneOf(v, `${path}.lod[${i}]`, LODS));
  for (const key of ['paint', 'ink']) if (L[key] !== undefined) paint(c, L[key], `${path}.${key}`, theme);
  switch (l.type) {
    case 'image':
      if ((l.art === undefined) === (l.asset === undefined)) c.error(path, 'An image layer has exactly one of art or asset.');
      if (l.art !== undefined) c.string(l.art, `${path}.art`, 32, ID);
      if (l.asset !== undefined) c.string(l.asset, `${path}.asset`, 160);
      if (l.fit !== undefined) c.oneOf(l.fit, `${path}.fit`, ENUMS.fit!);
      break;
    case 'text':
      bindingOk(c, l.text, `${path}.text`, theme);
      if (l.rich !== undefined && !richAllowed) c.error(`${path}.rich`, 'Rich text requires the rich-text presentation capability.');
      if (l.rich !== undefined && c.string(l.rich, `${path}.rich`, 8192)) {
        try {
          for (const [i, node] of parseRichText(l.rich).entries()) {
            if (node.type === 'value') bindingOk(c, node.binding, `${path}.rich.value[${i}]`, theme);
            if (back && node.type === 'value' && /^(object\.|fields|catalog\.|asset:.*\{)/.test(node.binding)) c.error(`${path}.rich.value[${i}]`, 'A back cannot use object or catalog bindings.');
            if (node.type === 'icon') icon(c, node.key, `${path}.rich.icon[${i}]`, theme);
          }
        } catch (error) { c.error(`${path}.rich`, (error as Error).message); }
      }
      if (l.padding !== undefined && c.array(l.padding, `${path}.padding`, 4, 4)) l.padding.forEach((n, i) => c.number(n, `${path}.padding[${i}]`, 0, 100));
      if (l.lineHeight !== undefined) c.number(l.lineHeight, `${path}.lineHeight`, 0.8, 2);
      if (l.role !== undefined) c.oneOf(l.role, `${path}.role`, ENUMS.role!);
      if (l.align !== undefined) c.oneOf(l.align, `${path}.align`, ENUMS.align!);
      if (l.valign !== undefined) c.oneOf(l.valign, `${path}.valign`, ENUMS.valign!);
      if (l.fit !== undefined) c.oneOf(l.fit, `${path}.fit`, TEXT_FIT);
      if (l.style !== undefined) c.oneOf(l.style, `${path}.style`, TEXT_STYLE);
      if (l.scale !== undefined) c.number(l.scale, `${path}.scale`, 0.5, 2);
      if (l.effects !== undefined && c.array(l.effects, `${path}.effects`, 0, 3)) l.effects.forEach((e, i) => effect(c, e, `${path}.effects[${i}]`));
      break;
    case 'icon': icon(c, l.icon, `${path}.icon`, theme); break;
    case 'icon-row':
      if (c.string(l.source, `${path}.source`, 160) && !/^(catalog\.)?fields\|costRow:/.test(l.source) && !/\|gains$/.test(l.source) && !/^fields\|stats:/.test(l.source)) c.error(`${path}.source`, 'Use fields|costRow:PREFIX, catalog.fields|costRow:PREFIX, fields|stats:KEYS, or a binding ending in |gains.');
      else bindingOk(c, l.source, `${path}.source`, theme);
      if (l.iconSize !== undefined) c.number(l.iconSize, `${path}.iconSize`, 1, 100);
      if (l.gap !== undefined) c.number(l.gap, `${path}.gap`, 0, 50);
      if (l.align !== undefined) c.oneOf(l.align, `${path}.align`, ENUMS.align!);
      if (l.style !== undefined) c.oneOf(l.style, `${path}.style`, ROW_STYLE);
      if (l.numberKeys !== undefined && c.array(l.numberKeys, `${path}.numberKeys`, 0, 12)) l.numberKeys.forEach((key, i) => c.string(key, `${path}.numberKeys[${i}]`, 128, ID));
      break;
    case 'badge':
      if (l.text === undefined && l.icon === undefined) c.error(path, 'A badge needs text or an icon.');
      if (l.text !== undefined) bindingOk(c, l.text, `${path}.text`, theme);
      if (l.icon !== undefined) icon(c, l.icon, `${path}.icon`, theme);
      if (l.shape !== undefined) c.oneOf(l.shape, `${path}.shape`, BADGE_SHAPES);
      if (l.effects !== undefined && c.array(l.effects, `${path}.effects`, 0, 3)) l.effects.forEach((e, i) => effect(c, e, `${path}.effects[${i}]`));
      break;
    case 'frame':
      if (l.style !== undefined) c.oneOf(l.style, `${path}.style`, FRAME_STYLES);
      if (l.width !== undefined) c.number(l.width, `${path}.width`, 0, 20);
      break;
    case 'shape':
      c.oneOf(l.shape, `${path}.shape`, SHAPES);
      if (l.stroke !== undefined && c.object(l.stroke, `${path}.stroke`, ['paint', 'width'])) { paint(c, l.stroke.paint, `${path}.stroke.paint`, theme); c.number(l.stroke.width, `${path}.stroke.width`, 0, 20); }
      break;
    case 'pattern':
      c.oneOf(l.pattern, `${path}.pattern`, PATTERNS);
      if (l.scale !== undefined) c.number(l.scale, `${path}.scale`, 0.1, 10);
      break;
  }
  if (back) for (const key of ['text', 'icon', 'source', 'paint', 'ink', 'when', 'unless', 'asset'])
    if (typeof L[key] === 'string' && /^(object\.|fields|catalog\.|asset:.*\{)/.test(L[key] as string)) c.error(`${path}.${key}`, 'A back cannot use object or catalog bindings: face-down objects have none.');
}

function effect(c: Checker, e: unknown, path: string): void {
  if (!e || typeof e !== 'object' || Object.keys(e).length !== 1) { c.error(path, 'An effect has exactly one of outline, shadow, glow, emboss or gradient.'); return; }
  const [kind] = Object.keys(e);
  if (!['outline', 'shadow', 'glow', 'emboss', 'gradient'].includes(kind!)) c.error(path, `Unknown effect "${kind}".`);
}

export function validateFaces(value: unknown, theme?: ThemeV2, _assetIds?: Set<string>, capabilities?: readonly string[]): Issue[] {
  const c = new Checker('faces.json');
  if (!c.object(value, '$', ['faces'])) return c.issues;
  const file = value as unknown as FacesFile;
  if (!c.array(file.faces, '$.faces', 0, 512)) return c.issues;
  const byId = new Map<string, FaceDef>();
  file.faces.forEach((f, i) => {
    const p = `$.faces[${i}]`;
    if (!c.object(f, p, ['id'], ['size', 'match', 'layers', 'extends', 'add', 'replace', 'remove', 'back', 'lod'])) return;
    if (c.string(f.id, `${p}.id`, 128, ID)) {
      if (byId.has(f.id) || isBuiltin(f.id)) c.error(`${p}.id`, 'Duplicate or reserved face ID.');
      byId.set(f.id, f);
    }
    if (f.size !== undefined && c.object(f.size, `${p}.size`, ['w', 'h'], ['radius'])) {
      c.number(f.size.w, `${p}.size.w`, 1, 1000); c.number(f.size.h, `${p}.size.h`, 1, 1000);
      if (f.size.radius !== undefined) c.number(f.size.radius, `${p}.size.radius`, 0, 500);
    }
    if (f.extends === undefined && f.size === undefined) c.error(`${p}.size`, 'A face without extends needs a size.');
    if (f.extends === undefined && (f.add || f.replace || f.remove)) c.error(p, 'add, replace and remove need extends.');
    if (f.match !== undefined && c.object(f.match, `${p}.match`, [], ['kind', 'catalog', 'tags', 'zoneKind', 'face'])) {
      if (f.match.catalog !== undefined && c.array(f.match.catalog, `${p}.match.catalog`, 1, 256)) f.match.catalog.forEach((k, j) => c.string(k, `${p}.match.catalog[${j}]`, 128, ID));
      if (f.match.tags !== undefined && c.array(f.match.tags, `${p}.match.tags`, 1, 16)) f.match.tags.forEach((k, j) => c.string(k, `${p}.match.tags[${j}]`, 128, ID));
      if (f.match.face !== undefined) c.oneOf(f.match.face, `${p}.match.face`, ['up', 'down']);
    }
    const richAllowed = capabilities === undefined || capabilities.includes('rich-text');
    if (f.layers !== undefined && c.array(f.layers, `${p}.layers`, 1, 48)) f.layers.forEach((l, j) => layer(c, l, `${p}.layers[${j}]`, theme, false, false, richAllowed));
    if (f.add !== undefined && c.array(f.add, `${p}.add`, 1, 48)) f.add.forEach((l, j) => layer(c, l, `${p}.add[${j}]`, theme, true, false, richAllowed));
    if (f.replace !== undefined && c.object(f.replace, `${p}.replace`, [], Object.keys(f.replace))) for (const [k, l] of Object.entries(f.replace)) layer(c, l, `${p}.replace.${k}`, theme, false, false, richAllowed);
    if (f.remove !== undefined && c.array(f.remove, `${p}.remove`, 1, 48)) f.remove.forEach((r, j) => c.string(r, `${p}.remove[${j}]`, 64));
    if (f.back !== undefined && c.array(f.back, `${p}.back`, 1, 48)) f.back.forEach((l, j) => layer(c, l, `${p}.back[${j}]`, theme, false, true, richAllowed));
    if (f.lod !== undefined && c.object(f.lod, `${p}.lod`, [], LODS)) for (const [k, o] of Object.entries(f.lod)) {
      if (c.object(o, `${p}.lod.${k}`, [], ['layers', 'size']) && o!.layers !== undefined && c.array(o!.layers, `${p}.lod.${k}.layers`, 1, 48)) o!.layers.forEach((r, j) => c.string(r, `${p}.lod.${k}.layers[${j}]`, 64));
    }
  });
  // Inheritance: parents exist, no cycles, every edit reference exists, and the result has layers.
  for (const f of byId.values()) {
    try {
      const flat = flatten(f.id, byId);
      if (flat.layers.length === 0 || flat.layers.length > 48) c.error(`face ${f.id}`, 'A resolved face needs 1 to 48 layers.');
      for (const [k, o] of Object.entries(f.lod ?? {})) for (const ref of o?.layers ?? []) {
        if (!flat.layers.some((l, i) => l.id === ref || `${l.type}#${flat.layers.slice(0, i + 1).filter(x => x.type === l.type).length}` === ref)) c.error(`face ${f.id}.lod.${k}`, `Unknown layer "${ref}".`);
      }
    } catch (e) { c.error(`face ${f.id}`, (e as Error).message); }
  }
  return c.issues;
}

/** Warnings: catalog entries no face matches, and bindings that resolve for no matched entry. */
export function validateFaceReferences(file: FacesFile, catalog: { key: string; kind: string; tags?: string[]; fields?: Record<string, unknown> }[]): Issue[] {
  const c = new Checker('faces references');
  const faces = file.faces.filter(f => f.match);
  for (const entry of catalog) {
    const f = matchFace(faces.map(x => ({ ...x, match: x.match! })), { kind: entry.kind, catalogKey: entry.key, tags: entry.tags ?? [], faceDown: false }, null);
    if (!f) c.warn(`catalog ${entry.key}`, 'No face matches this catalog entry; the v1 template or default shape is used.');
  }
  return c.issues;
}

// ---- Atlases and art (spec section 5, check 3) ------------------------------------------------

const FRAME_NAME = /^[A-Za-z0-9][A-Za-z0-9_.\-]{0,127}$/;

/** `assets`: manifest asset id to its pixel size (the sheets). */
export function validateAtlases(value: unknown, assets: Map<string, { width: number; height: number }>, missingIsWarning = false): Issue[] {
  const c = new Checker('atlases.json');
  if (!c.object(value, '$', ['atlases'])) return c.issues;
  const file = value as unknown as AtlasesFile;
  if (!c.array(file.atlases, '$.atlases', 0, 64)) return c.issues;
  const ids = new Set<string>();
  file.atlases.forEach((a, i) => {
    const p = `$.atlases[${i}]`;
    if (!c.object(a, p, ['id', 'asset'], ['frames', 'grid', 'names'])) return;
    if (c.string(a.id, `${p}.id`, 64, FRAME_NAME)) { if (ids.has(a.id)) c.error(`${p}.id`, 'Duplicate atlas ID.'); ids.add(a.id); }
    const sheet = c.string(a.asset, `${p}.asset`, 128) ? assets.get(a.asset) : undefined;
    if (typeof a.asset === 'string' && !sheet) {
      if (missingIsWarning) c.warn(`${p}.asset`, 'Hosted atlas unavailable; using fallback.');
      else c.error(`${p}.asset`, `Asset "${a.asset}" is not in the manifest.`);
    }
    if ((a.frames === undefined) === (a.grid === undefined)) { c.error(p, 'An atlas has exactly one of frames or grid.'); return; }
    const inside = (fp: string, x: number, y: number, w: number, h: number) => {
      if (sheet && (x + w > sheet.width || y + h > sheet.height)) c.error(fp, `Lies outside the ${sheet.width}x${sheet.height} sheet.`);
    };
    if (a.frames !== undefined && c.object(a.frames, `${p}.frames`, [], Object.keys(a.frames ?? {}))) {
      const names = Object.keys(a.frames);
      if (names.length > 4096) c.error(`${p}.frames`, 'At most 4,096 frames.');
      for (const name of names) {
        const fp = `${p}.frames.${name}`, f = a.frames[name]!;
        if (!FRAME_NAME.test(name)) c.error(fp, 'Frame names use letters, digits, dot, dash and underscore.');
        if (!c.object(f, fp, ['x', 'y', 'w', 'h'], ['pivot'])) continue;
        const ok = c.number(f.x, `${fp}.x`, 0, 4096, true) && c.number(f.y, `${fp}.y`, 0, 4096, true) && c.number(f.w, `${fp}.w`, 1, 4096, true) && c.number(f.h, `${fp}.h`, 1, 4096, true);
        if (ok) inside(fp, f.x, f.y, f.w, f.h);
        if (f.pivot !== undefined && (!Array.isArray(f.pivot) || f.pivot.length !== 2 || f.pivot.some(v => typeof v !== 'number' || v < 0 || v > 1))) c.error(`${fp}.pivot`, 'Expected [x, y] in 0..1.');
      }
    }
    if (a.grid !== undefined && c.object(a.grid, `${p}.grid`, ['cell', 'columns'])) {
      const cell = a.grid.cell;
      const cw = typeof cell === 'number' ? cell : cell?.w, ch = typeof cell === 'number' ? cell : cell?.h;
      const ok = c.number(cw, `${p}.grid.cell`, 1, 4096, true) && c.number(ch, `${p}.grid.cell`, 1, 4096, true) && c.number(a.grid.columns, `${p}.grid.columns`, 1, 4096, true);
      if (c.array(a.names, `${p}.names`, 1, 4096)) {
        const seen = new Set<string>();
        a.names!.forEach((n, j) => { if (c.string(n, `${p}.names[${j}]`, 128, FRAME_NAME)) { if (seen.has(n)) c.error(`${p}.names[${j}]`, 'Duplicate frame name.'); seen.add(n); } });
        if (ok) inside(`${p}.grid`, 0, 0, Math.min(a.names!.length, a.grid.columns) * cw!, Math.ceil(a.names!.length / a.grid.columns) * ch!);
      }
    } else if (a.names !== undefined) c.error(`${p}.names`, 'names belongs to a grid atlas.');
  });
  return c.issues;
}

/**
 * art.json: declared slots, rules and values. A frame reference that does not exist is an error
 * for the authoring validator (`strict`) and a warning for the client loader, because a missing
 * frame falls back and never blocks play.
 */
export function validateArt(value: unknown, atlases: AtlasesFile | null, strict: boolean, assets?: Set<string>): Issue[] {
  const c = new Checker('art.json');
  if (!c.object(value, '$', ['slots', 'assign'], ['fallback'])) return c.issues;
  const file = value as unknown as ArtFile;
  const slots = new Set<string>();
  if (c.array(file.slots, '$.slots', 1, 16)) file.slots.forEach((s, i) => { if (c.string(s, `$.slots[${i}]`, 32, ID)) { if (slots.has(s)) c.error(`$.slots[${i}]`, 'Duplicate slot.'); slots.add(s); } });
  const frames = frameIndex(atlases);
  const valueOk = (v: unknown, path: string) => {
    if (!c.string(v, path, 200)) return;
    const t = v as string;
    if (t === 'none') return;
    if (t.startsWith('asset:')) {
      if (!/^[A-Za-z0-9][A-Za-z0-9:_.\/-]{0,127}$/.test(t.slice(6))) c.error(path, 'Invalid asset reference.');
      else if (assets && !assets.has(t.slice(6))) {
        if (strict) c.error(path, `Asset "${t.slice(6)}" does not exist.`); else c.warn(path, 'Asset is unavailable; using fallback.');
      }
      return;
    }
    if (t.startsWith('pattern:')) { if (!(ART_PATTERNS as readonly string[]).includes(t.slice(8))) c.error(path, `Unknown pattern; use ${ART_PATTERNS.join(', ')}.`); return; }
    if (!/^[^/]+\/[^/]+$/.test(t)) { c.error(path, 'Use asset:ID, atlasId/frame, none or pattern:<name>.'); return; }
    if (frames.has(t)) return;
    if (strict) c.error(path, `Frame "${t}" does not exist; the slot falls back.`);
    else c.warn(path, `Frame "${t}" does not exist; the slot falls back.`);
  };
  if (c.array(file.assign, '$.assign', 0, 1024)) file.assign.forEach((r, i) => {
    const p = `$.assign[${i}]`;
    if (!r || typeof r !== 'object') { c.error(p, 'Expected an object.'); return; }
    if (c.object(r.match, `${p}.match`, [], ['kind', 'tags', 'catalog'])) {
      if (r.match.kind !== undefined) c.string(r.match.kind, `${p}.match.kind`, 64, ID);
      if (r.match.tags !== undefined && c.array(r.match.tags, `${p}.match.tags`, 1, 16)) r.match.tags.forEach((t, j) => c.string(t, `${p}.match.tags[${j}]`, 128, ID));
      if (r.match.catalog !== undefined && c.array(r.match.catalog, `${p}.match.catalog`, 1, 256)) r.match.catalog.forEach((k, j) => c.string(k, `${p}.match.catalog[${j}]`, 128, ID));
    }
    for (const [key, v] of Object.entries(r)) {
      if (key === 'match') continue;
      if (key === 'focus') {
        if (c.object(v, `${p}.focus`, [], [...slots])) for (const [s, f] of Object.entries(v as object)) {
          if (!Array.isArray(f) || f.length !== 2 || f.some(n => typeof n !== 'number' || n < 0 || n > 100)) c.error(`${p}.focus.${s}`, 'Expected [x, y] in 0..100.');
        }
        continue;
      }
      if (!slots.has(key)) { c.error(`${p}.${key}`, 'Not a declared slot.'); continue; }
      valueOk(v, `${p}.${key}`);
    }
  });
  if (file.fallback !== undefined && c.object(file.fallback, '$.fallback', [], [...slots])) for (const [s, v] of Object.entries(file.fallback)) valueOk(v, `$.fallback.${s}`);
  return c.issues;
}

/** Every image layer's art slot is declared in art.json (without art.json it draws nothing). */
export function validateArtSlots(file: FacesFile, art: ArtFile | null): Issue[] {
  const c = new Checker('faces.json');
  file.faces.forEach((f, i) => {
    const lists: [Layer[] | undefined, string][] = [[f.layers, 'layers'], [f.add, 'add'], [f.back, 'back'], [Object.values(f.replace ?? {}), 'replace']];
    for (const [list, name] of lists) (list ?? []).forEach((l, j) => {
      if (l.type !== 'image' || !l.art) return;
      if (!art) c.warn(`$.faces[${i}].${name}[${j}].art`, `Slot "${l.art}" draws nothing without art.json.`);
      else if (!art.slots.includes(l.art)) c.error(`$.faces[${i}].${name}[${j}].art`, `Slot "${l.art}" is not declared in art.json.`);
    });
  });
  return c.issues;
}
